import { auth, db } from '../config/firebase';
import { addDoc, collection, collectionGroup, deleteDoc, doc, getDoc, getDocs, increment, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where, onSnapshot,Unsubscribe   } from 'firebase/firestore';
import { AskMessage, ChatMessage, ChatRoom, User } from '../models/models';
import { get } from 'http';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const FIRESTORE_DB_PRODUCTION = process.env.REACT_APP_FIRESTORE_DB_PRODUCTION || '(default)';
const FIRESTORE_DB_DEV = process.env.REACT_APP_FIRESTORE_DB_DEV || 'mou-dev';
const ENV = process.env.REACT_APP_ENV || FIRESTORE_DB_PRODUCTION;

const FIRESTORE_DB_HEADER_VALUE =
  (ENV === 'prod' ? FIRESTORE_DB_PRODUCTION : FIRESTORE_DB_DEV).trim();

const getAccessToken = async (): Promise<string | undefined> => {
    try {
        const token = await auth.currentUser?.getIdToken();
        return token;
    } catch (error) {
        console.error('Error getting access token: ', error);
        return undefined;
    }
};

let currentUser:User|null = null

export const getUser = async (): Promise<User | null> => {

    if(currentUser){
        //console.log('cache currentUser', currentUser);
        return currentUser;
    }

    try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            //console.error('User not authenticated');
            return null;
        }

        const usersRef = collectionGroup(db, 'Users');
        const q = query(
            usersRef, 
            where('id', '==', userId),
            //where('role', 'array-contains', 'user')
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            currentUser = userDoc.data() as User;
            if(!currentUser.categories){
                currentUser.categories = [];
            }
            return currentUser;
        }

        console.error('User not found');
        return null;
    } catch (error) {
        console.error('Error getting user: ', error);
        return null;
    }
};
export const ratePrompt = async (newRateValue: number, chatRoomId: string, messageId: string) => {
    const messageRef = doc(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms/${chatRoomId}/ChatMessages/${messageId}`);
    const messageDoc = await getDoc(messageRef);
    if (!messageDoc.exists()) {
        throw new Error("Mensaje no encontrado");
    }
    const messageData = messageDoc.data();
    const promptId = messageData.promptId;

    // Referencia global del prompt del cliente
    const clientPromptRef = doc(db, `Clients/${currentUser!.clientId}/Prompts`, promptId);

    // Referencia a nivel de user del prompt
    const userPromptRef = doc(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/Prompts`, promptId);

    try {
        await runTransaction(db, async (transaction) => {
            // Obtener el documento global del prompt
            const promptDoc = await transaction.get(clientPromptRef);
            if (!promptDoc.exists()) {
                throw new Error("Prompt global no encontrado");
            }

            // Obtener el documento del prompt a nivel del cliente
            const clientPromptDoc = await transaction.get(userPromptRef);
            if (!clientPromptDoc.exists()) {
                // Si el documento del prompt para el cliente no existe, crearlo
                transaction.set(userPromptRef, {
                    rateSum: 0,
                    rateCount: 0,
                    rateValue: 0
                });
            }

            // Actualizar métricas globales
            const promptData = promptDoc.data();
            const rateSum = promptData.rateSum || 0;
            const newRateSum = rateSum + newRateValue;

            const rateCount = promptData.rateCount || 0;
            const newRateCount = rateCount + 1;
              
            const averageValue = newRateSum / newRateCount;      

            transaction.update(clientPromptRef, {
                rateSum: newRateSum,
                rateValue: averageValue,
                rateCount: newRateCount
            });

            // Actualizar métricas a nivel del cliente
            const clientPromptData = clientPromptDoc.data() || { rateSum: 0, rateCount: 0 };
            const clientRateSum = clientPromptData.rateSum || 0;
            const newClientRateSum = clientRateSum + newRateValue;

            const clientRateCount = clientPromptData.rateCount || 0;
            const newClientRateCount = clientRateCount + 1;

            const clientAverageValue = newClientRateSum / newClientRateCount;

            transaction.update(userPromptRef, {
                rateSum: newClientRateSum,
                rateValue: clientAverageValue,
                rateCount: newClientRateCount
            });

            // Marcar el mensaje como calificado
            transaction.update(messageRef, {
                rated: true
            });
        });

        return true;

    } catch (error) {
        console.error("Error al actualizar la calificación del prompt: ", error);
        return false;
    }
};





export const updateUser = async (user: User): Promise<boolean> => {
    try {
        const docRef = doc(db, `Clients/${user.clientId}/Users/${auth.currentUser?.uid}`);
        const updatedUser = {
            ...user,
            lastLogin: serverTimestamp(),
        };
        await updateDoc(docRef, updatedUser);
        return true;
    } catch (error) {
        console.error('Error updating user: ', error);
        return false;
    }
};

export const createChatRoom = async (chatRoom: ChatRoom): Promise<string | null> => {

    if(!auth.currentUser) { 
        console.error('currentUser is null in createChatRoom');
        currentUser = await getUser();
    }

    try {
        const collRef = collection(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms`);
        const docRef = await addDoc(collRef, {});
        const newChatRoom: ChatRoom = {
            ...chatRoom,
            id: docRef.id,
            createdAt: serverTimestamp(),
        };
        //console.log('newChatRoom', newChatRoom);
        await setDoc(docRef, newChatRoom);
        return newChatRoom.id;
    } catch (error) {
        console.error('Error creating chat room: ', error);
        return null;
    }
};

export const getChatRooms = async (): Promise<ChatRoom[]> => {
    if(!auth.currentUser) { 
        console.error('currentUser is null in getChatRooms');
        currentUser = await getUser();
    }
    try {
        const collRef = collection(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms`);
        const whereQuery = query(collRef, orderBy('createdAt', 'desc'));
        const response = await getDocs(whereQuery);
        return response.docs.map(doc => doc.data() as ChatRoom);
    } catch (error) {
        console.error('Error getting chat rooms: ', error);
        return [];
    }
};

export function subscribeChatRooms(
  clientId: string,
  uid: string,
  onChange: (rooms: ChatRoom[]) => void,
  onError?: (e: any) => void
): Unsubscribe {
  const collRef = collection(db, `Clients/${clientId}/Users/${uid}/ChatRooms`);
  const q = query(collRef, orderBy("createdAt", "desc"));

  return onSnapshot(
    q,
    (snap) => {
      const rooms: ChatRoom[] = snap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          name: data.name,
          userId: data.userId,
          createdAt: data.createdAt ?? null,
        } as ChatRoom;
      });
      onChange(rooms);
    },
    (err) => {
      console.error("listen chat rooms error:", err);
      onError?.(err);
    }
  );
}

export const deleteChatRoom = async (chatRoom: ChatRoom): Promise<boolean> => {

    if(!auth.currentUser) { 
        console.error('currentUser is null in deleteChatRoom');
        currentUser = await getUser();
    }

    try {
        const docRef = doc(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms/${chatRoom.id}`);
        await deleteDoc(docRef);
        return true;
    } catch (error) {
        console.error('Error deleting chat room: ', error);
        return false;
    }
};

export const createChatMessage = async (chatMessage: ChatMessage): Promise<string | null> => {

    if(!auth.currentUser) { 
        console.error('currentUser is null in createChatMessage');
        currentUser = await getUser();
    }

    try {
        const collRef = collection(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms/${chatMessage.chatRoomId}/ChatMessages`);
        const docRef = await addDoc(collRef, {});
        const newChatMessage: ChatMessage = {
            ...chatMessage,
            id: docRef.id,
            createdAt: serverTimestamp(),
        };
        await setDoc(docRef, newChatMessage);
        return docRef.id;
    } catch (error) {
        console.error('Error creating chat message: ', error);
        return null;
    }
};

export const getMessages = async (chatRoom: ChatRoom): Promise<ChatMessage[]> => {

    if(!auth.currentUser || !chatRoom.id || !chatRoom.userId) { 
        return [];
    }

    if(!currentUser){
        console.error('currentUser is null in getMessages');
        currentUser = await getUser();
    }

    try {
        const collRef = collection(db, `Clients/${currentUser!.clientId}/Users/${auth.currentUser?.uid}/ChatRooms/${chatRoom.id}/ChatMessages`);
        const whereQuery = query(collRef, orderBy('createdAt', 'asc'));
        const response = await getDocs(whereQuery);
        return response.docs.map(doc => doc.data() as ChatMessage);
    } catch (error) {
        console.error('Error getting messages: ', error);
        return [];
    }
};

export const ask = async (
    askMessage: AskMessage,
    onStream: (content: string) => void,
    signal: AbortSignal
): Promise<void> => {
    const accessToken = await getAccessToken();
    if (!accessToken) {
        throw new Error('ask error: Could not get access token, login again.');
    }

    //console.log('askMessage', JSON.stringify(askMessage));
    //console.log('accessToken', accessToken);

    const response = await fetch(`${API_BASE_URL}/ask`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'x-firestore-db': FIRESTORE_DB_HEADER_VALUE,
        },
        body: JSON.stringify(askMessage),
        signal,
    });

    if (!response.ok) {
        throw new Error('Network response was not ok');
    }

    if (!response.body) {
        throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let done = false;

    try {
        while (!done) {
            if (signal.aborted) {
                //console.log('Request aborted by user');
                reader.cancel();
                break;
            }

            const { value, done: doneReading } = await reader.read();
            done = doneReading;
            const chunk = decoder.decode(value, { stream: true });
            onStream(chunk);
        }
    } catch (error) {
        if (signal.aborted) {
            console.log('Request was aborted');
        } else {
            console.error('Error while reading the stream', error);
            throw error;
        }
    }
};
